import { useEffect, useMemo, useState } from 'react';
import GroupModal from '../../../components/GroupModal';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../../components/Header';
import {
  createShiftRosterGroup,
  getShiftRosterGroups,
  updateShiftRosterGroup,
  deleteShiftRosterGroup,
} from '../../../services/shiftRoasterService';
import { getUsers } from '../../../services/userService';
import Select from 'react-select';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import Loader from '../../../components/Loader/Loader';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';
interface Member {
  name: string;
  role: string;
}

interface Group {
  id: number;
  title: string;
  description: string;
  users: any[];
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Avatar({ name }: { name: string; role: string }) {
  return (
    <div className="bg-blue-100 text-blue-800 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
      {getInitials(name)}
    </div>
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ open, onClose, title, children }: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-80" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

interface GroupCardProps {
  group: any;
  onAddMember: () => void;
  onRemoveMember: (memberIndex: number) => void;
  onEditGroup: (group: any) => void;
  onDeleteGroup: () => void;
}

function GroupCard({ group, onAddMember, onRemoveMember, onEditGroup, onDeleteGroup }: GroupCardProps) {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      {/* <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">{group.title}</h2>

        <button className="text-gray-500 hover:text-gray-700" onClick={() => onEditGroup(group)}>
          ⋮
        </button>
      </div> */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">{group.title}</h2>

        <div className="relative">
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              setGroupMenuOpen((prev) => !prev);
            }}
          >
            ⋮
          </button>

          {groupMenuOpen && (
            <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
              <button
                onClick={() => {
                  onEditGroup(group);
                  setGroupMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Update
              </button>

              <button
                onClick={() => {
                  onDeleteGroup();
                  setGroupMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {group.users?.map((member: any, mi: number) => (
          <div key={mi} className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-2 py-2">
            <Avatar name={member.user?.name || ''} role={member.user?.user_type?.title || ''} />

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">{member.user?.name}</p>

              <p className="text-[11px] text-gray-500">{member.user?.user_type?.title}</p>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onAddMember}
        className="mt-2 w-full border border-dashed border-gray-300 rounded-lg py-2 text-gray-400 hover:border-red-400 hover:text-red-400 hover:bg-red-50 transition-colors flex items-center justify-center text-xl"
        aria-label={`Add member to ${group.title}`}
      >
        +
      </button>
    </div>
  );
}

export default function GroupManagement() {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [addGroupOpen, setAddGroupOpen] = useState(false);

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [targetGroupIndex, setTargetGroupIndex] = useState<number | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedAddUsers, setSelectedAddUsers] = useState<any[]>([]);

  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroupToDelete, setSelectedGroupToDelete] = useState<any>(null);

  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [errorModal, setErrorModal] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    title: '',
    message: '',
  });

  const location = useLocation();

  const activeTab = location.pathname.includes('/dutycode') ? 'Duty Code' : 'Group';

  const editGroupInitialData = useMemo(() => {
    if (!selectedGroup) return undefined;

    return {
      title: selectedGroup.title ?? selectedGroup.group_name ?? '',
      departmentId: selectedGroup.department_id ?? selectedGroup.department?.id ?? '',
      description: selectedGroup.description ?? '',
      personnel: (selectedGroup.users ?? selectedGroup.members ?? []).map((member: any) => {
        const user = member.user ?? member;

        return {
          id: member.user_id ?? user.id,
          name: user.name ?? member.name ?? '',
          role: user.user_type?.title ?? member.role ?? user.role ?? '',
        };
      }),
    };
  }, [selectedGroup]);

  function handleOpenAddMember(gi: number) {
    setTargetGroupIndex(gi);

    const group = groups[gi];

    // Already existing members
    const alreadySelected =
      group.users?.map((member: any) => ({
        value: member.user.id,
        label: `${member.user.name} (${member.user.user_type?.title || ''})`,
      })) || [];

    setSelectedAddUsers(alreadySelected);

    setAddMemberOpen(true);
  }

  const handleAddMember = async () => {
    if (targetGroupIndex === null) return;

    try {
      const group = groups[targetGroupIndex];

      const payload = {
        title: group.title,
        department_id: group.department_id,
        description: group.description,
        users: selectedAddUsers.map((user: any) => ({
          user_id: user.value,
        })),
      };

      await updateShiftRosterGroup(group.id, payload);

      setAddMemberOpen(false);

      await fetchGroups();

      setSuccessMessage({
        title: 'Success',
        message: 'Members added successfully!',
      });
      setSuccessModal(true);
    } catch (error: any) {
      console.error(error);

      const response = error?.response?.data;

      let message = 'Something went wrong.';

      // Validation errors
      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      }
      // API message (like route not found)
      else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Failed to Add Members',
        message,
      });

      setErrorModal(true);
    }
  };

  const handleEditGroup = (group: any) => {
    setSelectedGroup(group);
    setEditGroupOpen(true);
  };

  const handleUpdateGroup = async (payload: any) => {
    if (!selectedGroup) return;

    try {
      await updateShiftRosterGroup(selectedGroup.id, payload);

      await fetchGroups();

      setSuccessMessage({
        title: 'Success',
        message: 'Group updated successfully!',
      });

      setSuccessModal(true);
    } catch (error: any) {
      console.error(error);

      const response = error?.response?.data;

      let message = 'Something went wrong.';

      // Validation errors
      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      }
      // Backend message (404, 500, etc.)
      else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Update Failed',
        message,
      });

      setErrorModal(true);
      throw error;
    }
  };

  function handleRemoveMember(gi: number, mi: number) {
    setGroups((prev) => prev.map((g, i) => (i === gi ? { ...g, members: g.members.filter((_, idx) => idx !== mi) } : g)));
  }
  const navigate = useNavigate();

  const handleClick = (tab: string) => {
    if (tab === 'Group') {
      navigate('/ShiftRoster/configuration');
    }

    if (tab === 'Duty Code') {
      navigate('/ShiftRoster/configuration/dutycode');
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);

      const response = await getShiftRosterGroups();

      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };
  const fetchUsers = async () => {
    try {
      const response = await getUsers();

      setUsers(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, []);

  const handleCreateGroup = async (payload: any) => {
    try {
      await createShiftRosterGroup(payload);

      setAddGroupOpen(false);

      await fetchGroups();

      setSuccessMessage({
        title: 'Success',
        message: 'Group created successfully!',
      });

      setSuccessModal(true);
    } catch (error: any) {
      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Creation Failed',
        message,
      });

      setErrorModal(true);

      console.error('Error creating group:', error);
    }
  };

  const handleDeleteGroupClick = (group: any) => {
    setSelectedGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const confirmDeleteGroup = async () => {
    if (!selectedGroupToDelete) return;
    setIsDeleting(true);
    try {
      await deleteShiftRosterGroup(selectedGroupToDelete.id);

      setShowDeleteModal(false);
      setSelectedGroupToDelete(null);

      await fetchGroups();

      setSuccessMessage({
        title: 'Deleted',
        message: 'Group deleted successfully!',
      });
      setSuccessModal(true);
    } catch (error) {
      console.error('Error deleting group:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      <Header title="Shift Roster Configuration" showBackButton />

      <div className="flex-1 flex gap-4 p-6 overflow-hidden">
        {/* Left column: Tabs + Groups */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Tabs + Add Group */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex gap-2">
              {(['Group', 'Duty Code'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleClick(tab)}
                  className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-red-50 border-red-400 text-red-500'
                      : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  {tab === 'Group' ? 'Master Shift Personnel' : tab}
                </button>
              ))}
            </div>

            <button
              onClick={() => setAddGroupOpen(true)}
              className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <span className="text-base leading-none">+</span>
              Group
            </button>
          </div>

          {/* Groups Grid */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups.map((group, gi) => (
                  <GroupCard
                    key={gi}
                    group={group}
                    onAddMember={() => handleOpenAddMember(gi)}
                    onRemoveMember={(mi) => handleRemoveMember(gi, mi)}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={() => handleDeleteGroupClick(group)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Group Modal */}
      <GroupModal isOpen={addGroupOpen} onClose={() => setAddGroupOpen(false)} onSubmit={handleCreateGroup} />

      {/* Add Member Modal */}
      <Modal
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        title={targetGroupIndex !== null ? `Add members to ${groups[targetGroupIndex]?.title}` : 'Add Members'}
      >
        <div className="space-y-4">
          <Select
            isMulti
            closeMenuOnSelect={false}
            placeholder="Select Users"
            options={users.map((user: any) => ({
              value: user.id,
              label: `${user.name} (${user.user_type?.title || ''})`,
            }))}
            value={selectedAddUsers}
            onChange={(selected: any) => setSelectedAddUsers(selected || [])}
          />

          <div className="flex justify-end gap-2">
            <button onClick={() => setAddMemberOpen(false)} className="px-4 py-2 border rounded-lg">
              Cancel
            </button>

            <button onClick={handleAddMember} className="px-4 py-2 bg-red-500 text-white rounded-lg">
              Add
            </button>
          </div>
        </div>
      </Modal>

      {editGroupOpen && selectedGroup && (
        <GroupModal
          isOpen={editGroupOpen}
          onClose={() => {
            setEditGroupOpen(false);
            setSelectedGroup(null);
          }}
          onSubmit={handleUpdateGroup}
          initialData={editGroupInitialData}
        />
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[400px] p-6">
            <h2 className="text-lg font-semibold mb-3">Delete Confirmation</h2>

            <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete "{selectedGroupToDelete?.title}"?</p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeleting}
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedGroupToDelete(null);
                }}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md flex items-center justify-center gap-2 min-w-[90px] disabled:bg-red-400 disabled:cursor-not-allowed"
                onClick={confirmDeleteGroup}
                disabled={isDeleting}
              >
                {isDeleting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />

      <ErrorModal
        isOpen={errorModal}
        onClose={() => setErrorModal(false)}
        title={errorMessage.title}
        message={errorMessage.message}
        buttonText="OK"
        onButtonClick={() => setErrorModal(false)}
      />
    </div>
  );
}
